import { AddPropertyReview } from "@services/krent.service";
import { formatDateTime } from "@utils/helpers";
import { useSessionStorage } from "hooks";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getAllPropertyReviews } from "@services/krent.service";

export default function RatingsAndReviews({
  propertyId,
  showPostReview = false,
}: {
  propertyId: string;
  showPostReview?: boolean;
}) {
  const [user] = useSessionStorage("user", {});
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [loading, setLoading] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [activeIndicator, setActiveIndicator] = useState(0);
  const [reviews, setReviews] = useState<any[]>([]);
  const [reviewStats, setReviewStats] = useState<any>(null);
  const navigate = useNavigate();

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const handleScroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 320;

      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScrollUpdate = () => {
      const scrollLeft = container.scrollLeft;
      const cardWidth = 250;
      const activeIndex = Math.round(scrollLeft / cardWidth);
      setActiveIndicator(activeIndex);
    };

    container.addEventListener("scroll", handleScrollUpdate);
    return () => {
      container.removeEventListener("scroll", handleScrollUpdate);
    };
  }, []);

  useEffect(() => {
    const fetchReviews = async () => {
      if (!propertyId) {
        setReviewsLoading(false);
        return;
      }
      try {
        const abortController = new AbortController();
        const response: any = await getAllPropertyReviews(
          propertyId,
          1,
          50,
          abortController.signal,
        );
        const reviewsData = response.data || [];
        setReviews(reviewsData);

        if (reviewsData.length > 0) {
          const totalRating = reviewsData.reduce(
            (sum: number, r: any) => sum + r.rating,
            0,
          );
          const averageRating = totalRating / reviewsData.length;
          setReviewStats({
            averageRating,
            total: reviewsData.length,
          });
        } else {
          setReviewStats({
            averageRating: 0,
            total: 0,
          });
        }

        setReviewsLoading(false);
      } catch (error) {
        setReviews([]);
        setReviewStats({
          averageRating: 0,
          total: 0,
        });
        setReviewsLoading(false);
      }
    };

    fetchReviews();
  }, [propertyId]);

  const handlePostReview = async () => {
    if (!rating || !reviewText.trim()) {
      toast.error("Please provide both a rating and a review.");
      return;
    }

    setLoading(true);

    try {
      const response: any = await AddPropertyReview(propertyId, {
        review: reviewText,
        rating,
      });

      if (!response?.errors) {
        setReviewText("");
        setRating(0);

        toast.success("Review posted successfully for review!");
      } else {
        toast.error(response?.errors?.[0]?.message || "Failed to post review");
      }
    } catch (err: any) {
      toast.error(
        err?.response?.errors?.[0]?.message || "Failed to post review",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container bg-white rounded">
      <div className="d-flex align-items-center mb-2 items-center gap-2">
        <i className="iconly-Star icbo fs-6" style={{ color: "#000" }} />
        {reviewStats?.total && reviewStats?.total > 0 ? (
          <h3 className="fw-bold mb-0 fs-5">
            {Number(reviewStats?.averageRating || 0).toFixed(1)}
          </h3>
        ) : (
          ""
        )}

        <div
          className="fw-bold fs-4 rounded-full"
          style={{
            backgroundColor: "#000",
            width: "4px",
            height: "4px",
          }}
        />

        <small className="text-muted fw-semibold">
          {reviewStats?.total || 0} reviews
        </small>

        {reviews && reviews.length > 1 && (
          <div className="d-flex gap-2 ms-auto">
            <button
              className="btn btn-light rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: "32px", height: "32px" }}
              onClick={() => handleScroll("left")}
            >
              <i className="fa fa-chevron-left"></i>
            </button>

            <button
              className="btn btn-light rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: "32px", height: "32px" }}
              onClick={() => handleScroll("right")}
            >
              <i className="fa fa-chevron-right"></i>
            </button>
          </div>
        )}
      </div>

      <div className="mb-4">
        {reviewsLoading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="d-flex gap-2">
              <div
                className="rounded-circle bg-black"
                style={{
                  width: "8px",
                  height: "8px",
                  animation: "bounce 1.4s infinite ease-in-out both",
                }}
              />
              <div
                className="rounded-circle bg-black"
                style={{
                  width: "8px",
                  height: "8px",
                  animation: "bounce 1.4s infinite ease-in-out both 0.2s",
                }}
              />
              <div
                className="rounded-circle bg-black"
                style={{
                  width: "8px",
                  height: "8px",
                  animation: "bounce 1.4s infinite ease-in-out both 0.4s",
                }}
              />
            </div>
            <style>{`
              @keyframes bounce {
                0%, 80%, 100% { transform: scale(0); }
                40% { transform: scale(1); }
              }
            `}</style>
          </div>
        ) : reviews && reviews.length > 0 ? (
          <div
            ref={scrollContainerRef}
            className="d-flex gap-3 hide-scrollbar"
            style={{
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              paddingBottom: "10px",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {reviews.slice(0, 6).map((r: any) => {
              const reviewerName = r?.user
                ? (
                    `${r.user.firstname || ""} ${r.user.lastname || ""}`.trim() ||
                    r.user.preferredName ||
                    r.user.name ||
                    ""
                  ).trim()
                : "";

              return (
              <div
                key={r._id}
                style={{
                  minWidth: "250px",
                  maxWidth: "300px",
                  minHeight: "300px",
                  scrollSnapAlign: "start",
                }}
              >
                <div className="border rounded-4 p-3 h-100">
                  <div>
                    <span className="fw-semibold">
                      {reviewerName || "Anonymous"}
                    </span>
                  </div>

                  <small className="">{formatDateTime(r.createdAt)}</small>

                  <div className="d-flex my-4">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`iconly-Star icbo me-1 ${
                          i < r.rating ? "text-warning" : "text-secondary"
                        }`}
                      ></i>
                    ))}
                  </div>

                  <p className="text-muted mb-2 fs-14">{r.review}</p>
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <p className="text-muted">No reviews yet.</p>
        )}

        {reviews && reviews.length > 1 && (
          <div className="d-flex justify-content-center gap-2 mt-3">
            {reviews.slice(0, 6).map((_: any, index: number) => (
              <div
                key={index}
                className="rounded-circle"
                style={{
                  width: "8px",
                  height: "8px",
                  backgroundColor:
                    index === activeIndicator ? "#000" : "#d1d5db",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease",
                }}
                onClick={() => {
                  if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTo({
                      left: index * 250,
                      behavior: "smooth",
                    });
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>

      {reviews && reviews.length > 2 && (
        <>
          <div className="d-flex justify-content-end mb-4">
            <button
              className="btn btn-primary d-inline-flex align-items-center"
              onClick={() => navigate(`/all-reviews/${propertyId}`)}
            >
              View all ratings
              <i className="iconly-Arrow-Right icbo ms-2 text-white"></i>
            </button>
          </div>

          <hr />
        </>
      )}

      {showPostReview && user && user?.role === "user" && (
        <div>
          <div className="mb-3">
            <p className="fw-semibold mb-1">Click to rate:</p>

            <div className="d-flex align-items-center">
              {[...Array(5)].map((_, i) => {
                const starValue = i + 1;
                const isActive = starValue <= (hover || rating);

                return (
                  <i
                    key={starValue}
                    className={`iconly-Star icbo fs-4 me-1 ${
                      isActive ? "text-warning" : ""
                    }`}
                    style={{
                      cursor: "pointer",
                      color: isActive ? "#f4b400" : "transparent",
                      WebkitTextStroke: isActive ? "0" : "1px #20c997",
                      transition: "color 0.2s ease, WebkitTextStroke 0.2s ease",
                    }}
                    onMouseEnter={() => setHover(starValue)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(starValue)}
                  ></i>
                );
              })}

              {rating > 0 && (
                <small className="ms-2 text-muted">{rating} / 5</small>
              )}
            </div>
          </div>

          <div className="mb-3">
            <p className="fw-semibold mb-1">Post a review</p>

            <textarea
              className="form-control"
              rows={4}
              placeholder="Write your review here..."
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
            ></textarea>
          </div>

          <button
            className="btn btn-primary"
            onClick={handlePostReview}
            disabled={loading}
          >
            {loading ? "Posting..." : "Post review"}
          </button>
        </div>
      )}
    </div>
  );
}
